import { UserAttributeDefs } from '@iotv/datamodel-core'
import { Position, TimedPosition } from '@iotv/iotv-v3-types'
import { lineString, point } from '@turf/helpers'
import { circle, pointToLineDistance } from '@turf/turf'
import { Feature, GeoJsonProperties, LineString } from 'geojson'
import { EffluentSpreaderHelper } from '../utilityFns'
import { EFFLUENT_PATH_TYPE, EffluentPath } from './EffluentPath'
import { AreaData, EffluentTracker, RunStats } from './EffluentTracker'

const typeName = 'PivotEffluentSpreader'
const DEFAULT_THROW_RADIUS = 0
const dev = process.env.REACT_APP_DEBUG
const debug = dev && false

export class PivotEffluentSpreader extends EffluentTracker {
  // extends DeviceEnabledType<SupportedDevices> {
  constructor(ob: any) {
    super(ob)
    this.span = ob.span
    this.throwDistance = ob.throwDistance
    this.location = ob.location
    this.path = ob.path || []
    this.spreading = ob.spreading
    this.flow = ob.flow
    this.radius = ob.radius || DEFAULT_THROW_RADIUS
    this.type = typeName
  }

  throwDistance: number
  location?: Position
  path: TimedPosition[]
  spreading?: boolean
  flow?: number
  span: number
  radius: number

  /**
   * Updated relevant runs. Allows runs of type PivotEffluentSpreader
   * @param runList
   * @returns runs of correct run type
   */
  getRelevantRuns(runList: EffluentPath[]): EffluentPath[] {
    debug && console.log('Using pivot specific - getRelevantRuns')

    // return only travelling type
    let filteredRuns = runList.filter((run) => run.pathType === EFFLUENT_PATH_TYPE.PivotEffluentSpreader)
    return filteredRuns
  }

  /**
   * Given active path data, convert it into datapoints on the mapped run
   * - Should be used for Travelling and Lateral
   * @param run
   * @param path
   * @returns
   */
  getPathOnRun(path: Position[], run: EffluentPath | undefined): Position[] {
    debug && console.log('Using default - getPathOnRun')
    if (run === undefined) {
      return super.getPathOnRun(path)
    }
    // || condition for pivot which doesn't know the radius from the center
    return EffluentSpreaderHelper.mapCirclePoints(path, run.path[0], this.radius || run.radius, this.throwDistance)
  }

  /**
   * Should be for pivot only
   * Function to get the closest path of a run, given a set of runs
   * @param runs
   * @param path
   * @returns the path closests to the path of the spreader
   */
  getBestMatchedRun(runs: EffluentPath[], path: TimedPosition[] | undefined = this.path): RunStats | undefined {
    debug && console.log('Using default - getBestMatchedRun')

    // check there are runs to check against
    // check the path is of length
    if (runs.length === 0 || path === undefined || path.length === 0) {
      return undefined
    }

    //turf current path
    const currentPathTurf = EffluentSpreaderHelper.convertPositionToTurf(path).map((tf) => point(tf))

    let runScore: { distance: number; average: number; run: EffluentPath }[] = runs
      .map((run) => {
        let runPoint = run.path
        let turfedRun: Feature<LineString, GeoJsonProperties> | undefined

        if (run.pathType === EFFLUENT_PATH_TYPE.PivotEffluentSpreader && runPoint?.length === 1) {
          // construct line
          let potentialPoints = circle(EffluentSpreaderHelper.convertPositionToTurf(runPoint)[0], run.radius, { units: 'meters' }).geometry.coordinates[0]

          turfedRun = lineString(potentialPoints)
        } else {
          debug && console.log('FAILED. Path needs to be of type PivotEffluentSpreader')
          return undefined
        }

        let distance = currentPathTurf.reduce((acc: number, cur) => {
          let dis = pointToLineDistance(cur, turfedRun, { units: 'meters' })
          acc += dis
          return acc
        }, 0)

        return {
          distance,
          average: distance / (path.length || 1),
          run,
        }
      })
      .filter((val) => val !== undefined)
      .sort((a, b) => a.distance - b.distance)

    return runScore[0]
  }

  /**
   * Pivot Override
   * Function to get the spread LatLngs, and area (m^2), of the raw run
   * @returns LatLngs and area (m^2) obj
   */
  getSpreadOfRawRun(relevantRun?: EffluentPath, path: Position[] = this.path): AreaData | undefined {
    if (path && path.length > 0) {
      if (relevantRun && relevantRun.path.length === 1) {
        let positions = EffluentSpreaderHelper.mapCircleRun(
          path,
          relevantRun.path[0],
          relevantRun.throwDistance + relevantRun.radius,
          relevantRun.coverage,
          relevantRun.throwDistance,
        )
        if (positions) {
          let turfBoundary = EffluentSpreaderHelper.convertPositionToTurf(positions)
          let turfArea = EffluentSpreaderHelper.createArea([turfBoundary])
          let area = EffluentSpreaderHelper.getAreaMeter(turfArea)
          return { positions, area }
        }
      }
      return super.getSpreadOfRawRun(relevantRun, path)
    }
    return undefined
  }

  /**
   * Pivot - getSpreadOfMappedRun
   *
   * Should be ok to delete, as it esentially follows the base logic
   * @param relevantRun
   * @param path
   * @returns
   */
  getSpreadOfMappedRun(relevantRun: EffluentPath, path: Position[] = this.path) {
    debug && console.log('Using pivot specific - getSpreadOfMappedRun')

    let mappedPoints = path
    if (path.length > 1) {
      if (relevantRun) {
        mappedPoints = this.getPathOnRun(path, relevantRun)
      }
    }
    return this.getSpreadOfRawRun(relevantRun, mappedPoints)
  }

  getUserAttributes(): UserAttributeDefs {
    return {
      ...super.getUserAttributes(),
      singleDeviceControl: {
        type: 'Boolean',
        editable: true,
        default: false,
        label: 'Single Device Control',
        required: true,
      },
      span: {
        type: 'Number',
        editable: true,
        label: 'Pivot Span Length (m)',
        name: 'Pivot Span Length (m)',
        description: 'Pivot Span Length (m)',
        required: true,
      },
      location_lat: {
        type: 'Number',
        precision: 5,
        editable: false,
        label: 'Pivot Lat',
        limits: {
          min: -90,
          max: 90,
        },
        required: false,
      },
      location_lng: {
        type: 'Number',
        precision: 5,
        editable: false,
        label: 'Pivot Lng',

        limits: {
          min: -180,
          max: 180,
        },
        required: false,
      },
    }
  }
}
